import { TRANSITION } from '@/config/training';

/**
 * Measures how long a chord change actually took.
 *
 * The naive definition — "time between two audio peaks" — measures the
 * metronome, not the player: a peak appears on every strum whether or not the
 * chord underneath it is right. And using the moment the chroma settles is just
 * as wrong, because that carries the analyser's 682 ms window with it.
 *
 * So the measurement is **anchored on onsets and gated by stability**:
 *
 * 1. while the previous chord is confirmed stable, remember the time of the
 *    last strum that happened during it — that is `t0`, the moment the player
 *    was last definitely on the old chord;
 * 2. after the target changes, wait for the new chord to become stable;
 * 3. `t1` is the last strum at or before that confirmation — the strum that
 *    actually produced the new chord;
 * 4. the transition time is `t1 - t0`.
 *
 * Because both ends are onsets, the analyser's latency appears on both sides
 * and cancels out. Implausible values are discarded rather than reported.
 */
export class TransitionTimer {
  private onsets: number[] = [];
  /** Last strum that landed while the previous chord was confirmed. */
  private anchor: number | null = null;
  private anchorChordId: string | null = null;
  private pendingSince: number | null = null;

  registerOnset(time: number): void {
    this.onsets.push(time);
    // Keep a short tail; anything older than the slowest plausible transition
    // can never be an anchor again.
    const cutoff = time - TRANSITION.maxPlausibleSec * 2;
    while (this.onsets.length > 0 && this.onsets[0] < cutoff) this.onsets.shift();
  }

  /** Called whenever the exercise moves to a new chord. */
  beginTransition(fromChordId: string | null, atTime: number): void {
    this.pendingSince = atTime;
    if (fromChordId === null) this.anchor = null;
  }

  /**
   * Called when the *current* target has been confirmed stable.
   *
   * @returns the measured transition in seconds, or null when it cannot be
   *          measured honestly.
   */
  confirmStable(chordId: string, atTime: number): number | null {
    const strum = this.lastOnsetAtOrBefore(atTime);
    let measured: number | null = null;

    if (
      this.anchor !== null &&
      this.anchorChordId !== null &&
      this.anchorChordId !== chordId &&
      strum !== null &&
      this.pendingSince !== null &&
      // The strum that completed the change must come after the target changed,
      // otherwise we would be measuring a lucky early guess.
      strum >= this.pendingSince - TRANSITION.minPlausibleSec
    ) {
      const seconds = strum - this.anchor;
      if (seconds >= TRANSITION.minPlausibleSec && seconds <= TRANSITION.maxPlausibleSec) {
        measured = seconds;
      }
    }

    // This chord is now the anchor for the next change.
    this.anchor = strum;
    this.anchorChordId = chordId;
    return measured;
  }

  /** Called when a slot ended without the chord ever stabilising. */
  abandon(): void {
    this.anchor = null;
    this.anchorChordId = null;
    this.pendingSince = null;
  }


  reset(): void {
    this.onsets = [];
    this.abandon();
  }

  private lastOnsetAtOrBefore(time: number): number | null {
    for (let i = this.onsets.length - 1; i >= 0; i--) {
      if (this.onsets[i] <= time) return this.onsets[i];
    }
    return null;
  }
}
